import {
  Box,
  FieldSet,
  FillPreview,
  FormField,
  Input,
  SegmentedToggle,
  Text,
  Thumbnail
} from "@wix/design-system";
import { ButtonRounded } from "@wix/wix-ui-icons-common";
import lodash from "lodash";
import React, { useContext, useEffect, useRef, useState } from "react";
import { EditorContext, ModalServiceContext, RicosContext, ZIndexContext } from "./../../ricos-context";
import { ButtonData_Styles_ButtonSize } from "./../../ricos-schema/next-schema/generated/ricos_document";
import { useNodeAttrs } from "./../../_shared/use-node-attrs";
import { getHexColorFromAlpha } from "./../../wix-rich-content-common";
import { ButtonColorPicker } from "./../components/button-color-picker";
import { buttonsModals } from "./../constants";
import {
  ColorPickerLiveSiteColorHint,
  ColorWithOpacityInput,
  NumberInputWithLabel
} from "./../../ui-components";
import { SWATCH_TOGGLE } from "./../../ui-components/color-picker/button-color-swatches/data-hooks";
import {
  DESIGN_SETTINGS_BACKGROUND_COLOR_INPUT,
  DESIGN_SETTINGS_BORDER_COLOR_INPUT,
  DESIGN_SETTINGS_BORDER_WIDTH_INPUT,
  DESIGN_SETTINGS_CORNER_RADIUS_INPUT,
  DESIGN_SETTINGS_TEXT_COLOR_INPUT
} from "../data-hooks";
const defaultBorderRadius = 0;
const defaultBorderWidth = 0;
const BUTTON_NORMAL_STATE_TOGGLE_VALUE = "NORMAL";
const BUTTON_HOVER_STATE_TOGGLE_VALUE = "HOVER";
const DesignComponent = ({
  config,
  onChange,
  defaultBackgroundColor,
  defaultTextColor,
  nodeId,
  nodeType,
  logBi
}) => {
  const { t, isMobile, environment } = useContext(RicosContext);
  const modalService = useContext(ModalServiceContext);
  const { adapter } = useContext(EditorContext);
  const { pmNodeAttrs } = useNodeAttrs(nodeId, nodeType);
  const zIndexService = useContext(ZIndexContext);
  const zIndex = zIndexService.getZIndex("DIALOG");
  const backgroundPickerToggleRef = useRef(null);
  const textPickerToggleRef = useRef(null);
  const borderPickerToggleRef = useRef(null);
  const [buttonStateToggleValue, setButtonStateToggleValue] = useState("NORMAL");
  const isHoverState = buttonStateToggleValue === BUTTON_HOVER_STATE_TOGGLE_VALUE;
  const styles = pmNodeAttrs?.styles;
  const textColor = styles?.textColor ?? defaultTextColor;
  const borderColor = styles?.borderColor;
  const backgroundColor = styles?.backgroundColor ?? defaultBackgroundColor;
  const borderWidth = styles?.borderWidth ?? defaultBorderWidth;
  const borderRadius = styles?.borderRadius ?? defaultBorderRadius;
  const buttonSize = styles?.buttonSize ?? ButtonData_Styles_ButtonSize.MEDIUM;
  const textColorHover = styles?.textColorHover ?? textColor;
  const borderColorHover = styles?.borderColorHover ?? borderColor;
  const backgroundColorHover = styles?.backgroundColorHover ?? backgroundColor;
  const setHoverPreview = (applyPreview) => {
    adapter.tiptapEditor.chain().setHoverPreview(applyPreview).run();
  };
  useEffect(() => {
    setHoverPreview(isHoverState);
    return () => setHoverPreview(false);
  }, [isHoverState]);
  const handleOnChange = (newData) => {
    const newStyles = {
      ...styles,
      ...newData
    };
    onChange({
      styles: {
        ...newStyles,
        borderRadius: newStyles?.borderRadius === defaultBorderRadius ? void 0 : newStyles?.borderRadius,
        borderWidth: newStyles?.borderWidth === defaultBorderWidth ? void 0 : newStyles?.borderWidth
      }
    });
  };
  const showLiveSiteHint = environment !== "liveSite";
  const openColorPicker = (colorType) => {
    const referenceElement = getPickerToggleRef(colorType);
    const modalId = buttonsModals.colorPicker;
    modalService.togglePluginPanel({
      id: modalId,
      Component: ButtonColorPicker,
      componentProps: {
        modalId,
        nodeId,
        nodeType,
        colorType,
        isHoverState,
        referenceElement,
        config,
        defaultTextColor,
        defaultBackgroundColor,
        onColorSelected: onColorChange(colorType),
        onColorAdded: onColorAdded(colorType)
      },
      isMobile,
      positioning: {
        referenceElement,
        placement: "left"
      },
      type: void 0,
      onClickOutside: lodash.noop,
      focusReturnElement: referenceElement ?? void 0
    });
  };
  const onColorChange = (colorType) => (color) => handleOnChange({ [`${colorType}${isHoverState ? "Hover" : ""}`]: color });
  const onColorAdded = (colorType) => (color) => {
    if (colorType === "backgroundColor") {
      return config.onBackgroundColorAdded?.(color);
    } else if (colorType === "textColor") {
      return config.onTextColorAdded?.(color);
    } else {
      return config.onBorderColorAdded?.(color);
    }
  };
  const getPickerToggleRef = (colorType) => {
    if (colorType === "backgroundColor") {
      return backgroundPickerToggleRef.current;
    } else if (colorType === "textColor") {
      return textPickerToggleRef.current;
    } else {
      return borderPickerToggleRef.current;
    }
  };
  return /* @__PURE__ */ React.createElement(
    Box,
    {
      direction: "vertical",
      width: "100%",
      padding: "SP4",
      paddingTop: "SP3",
      paddingBottom: "SP3",
      gap: "SP2"
    },
    /* @__PURE__ */ React.createElement(FormField, { inputWidth: "100%" }, /* @__PURE__ */ React.createElement(
      SegmentedToggle,
      {
        defaultSelected: buttonStateToggleValue,
        size: "small",
        onClick: (_, value) => setButtonStateToggleValue(value)
      },
      /* @__PURE__ */ React.createElement(SegmentedToggle.Button, { value: BUTTON_NORMAL_STATE_TOGGLE_VALUE }, t("ButtonModal_Design_Normal_State_Toggle")),
      /* @__PURE__ */ React.createElement(SegmentedToggle.Button, { value: BUTTON_HOVER_STATE_TOGGLE_VALUE }, t("ButtonModal_Design_Hover_State_Toggle"))
    )),
    isHoverState ? null : /* @__PURE__ */ React.createElement(Box, { paddingTop: "SP1" }, /* @__PURE__ */ React.createElement(
      FormField,
      {
        label: t("ButtonModal_Button_Size"),
        labelSize: "small",
        labelPlacement: "left",
        labelWidth: "1fr",
        labelAlignment: "middle"
      },
      /* @__PURE__ */ React.createElement(Box, { direction: "horizontal", gap: "6px" }, /* @__PURE__ */ React.createElement(
        SizeOption,
        {
          selected: buttonSize === ButtonData_Styles_ButtonSize.SMALL,
          icon: /* @__PURE__ */ React.createElement(ButtonRounded, { size: 12 }),
          onClick: () => handleOnChange({ buttonSize: ButtonData_Styles_ButtonSize.SMALL })
        }
      ), /* @__PURE__ */ React.createElement(
        SizeOption,
        {
          selected: buttonSize === ButtonData_Styles_ButtonSize.MEDIUM,
          icon: /* @__PURE__ */ React.createElement(ButtonRounded, { size: 18 }),
          onClick: () => handleOnChange({ buttonSize: ButtonData_Styles_ButtonSize.MEDIUM })
        }
      ), /* @__PURE__ */ React.createElement(
        SizeOption,
        {
          selected: buttonSize === ButtonData_Styles_ButtonSize.LARGE,
          icon: /* @__PURE__ */ React.createElement(ButtonRounded, { size: 24 }),
          onClick: () => handleOnChange({ buttonSize: ButtonData_Styles_ButtonSize.LARGE })
        }
      ))
    )),
    /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP3" }, /* @__PURE__ */ React.createElement(
      ColorWithOpacityInput,
      {
        label: t("ButtonModal_Background_Color"),
        color: isHoverState ? backgroundColorHover : backgroundColor,
        onChange: onColorChange("backgroundColor"),
        onColorPress: () => openColorPicker("backgroundColor"),
        colorPickerRef: backgroundPickerToggleRef,
        inputDataHook: DESIGN_SETTINGS_BACKGROUND_COLOR_INPUT,
        zIndex
      }
    ), showLiveSiteHint && /* @__PURE__ */ React.createElement(ColorPickerLiveSiteColorHint, null)),
    /* @__PURE__ */ React.createElement(
      FieldSet,
      {
        legend: void 0,
        columns: "auto 30px",
        alignment: "center",
        dataHook: DESIGN_SETTINGS_TEXT_COLOR_INPUT
      },
      /* @__PURE__ */ React.createElement(Text, { size: "small" }, t("ButtonModal_Text_Color")),
      /* @__PURE__ */ React.createElement(
        FillPreview,
        {
          fill: isHoverState ? getHexColorFromAlpha(textColorHover) : getHexColorFromAlpha(textColor),
          onClick: () => openColorPicker("textColor"),
          ref: textPickerToggleRef,
          dataHook: SWATCH_TOGGLE
        }
      )
    ),
    /* @__PURE__ */ React.createElement(
      ColorWithOpacityInput,
      {
        label: t("ButtonModal_Border_Color"),
        color: isHoverState ? borderColorHover : borderColor,
        onChange: onColorChange("borderColor"),
        onColorPress: () => openColorPicker("borderColor"),
        colorPickerRef: borderPickerToggleRef,
        inputDataHook: DESIGN_SETTINGS_BORDER_COLOR_INPUT,
        zIndex
      }
    ),
    isHoverState ? null : /* @__PURE__ */ React.createElement(
      NumberInputWithLabel,
      {
        label: t("ButtonModal_Width_Input"),
        dataHook: DESIGN_SETTINGS_BORDER_WIDTH_INPUT,
        suffix: /* @__PURE__ */ React.createElement(Input.Affix, null, t("Common_Units_PX")),
        value: borderWidth,
        min: 0,
        max: 15,
        onChange: (width) => handleOnChange({ borderWidth: width }),
        logBi,
        zIndex
      }
    ),
    isHoverState ? null : /* @__PURE__ */ React.createElement(
      NumberInputWithLabel,
      {
        label: t("ButtonModal_Radius_Input"),
        dataHook: DESIGN_SETTINGS_CORNER_RADIUS_INPUT,
        suffix: /* @__PURE__ */ React.createElement(Input.Affix, null, t("Common_Units_PX")),
        value: borderRadius,
        min: 0,
        max: 30,
        onChange: (radius) => handleOnChange({ borderRadius: radius }),
        logBi,
        zIndex
      }
    )
  );
};
const SizeOption = ({
  icon,
  selected,
  onClick
}) => {
  return /* @__PURE__ */ React.createElement(
    Thumbnail,
    {
      height: 40,
      width: 40,
      size: "tiny",
      onClick,
      selected,
      noPadding: true,
      contentAlignment: "center"
    },
    /* @__PURE__ */ React.createElement(Box, { align: "center" }, icon)
  );
};
export {
  DesignComponent
};
//# sourceMappingURL=design-component.js.map
